/**
 * JSON-LD @graph generator for CIH V3 Playbook detail pages
 * (/coaching-business/playbooks/[slug]).
 *
 * Emits Article + Person (the thought leader who originated the framework) +
 * FAQPage (when FAQs present) + BreadcrumbList + Organization as a single
 * @graph. The Article is DCM's coaching-application analysis (author = DCM);
 * the Person is referenced via `about`/`creator` as the framework's originator
 * — never as the author, per the IP protocol (third-person, no endorsement).
 *
 * The Organization node is a stub that resolves to the canonical brand entity
 * via the shared BRAND_ORGANIZATION_ID; the fully-described Organization lives
 * in the site-wide schema. Both must use that exact @id or the Knowledge Graph
 * splits the brand into two entities.
 *
 * Aliases: playbook jsonld, playbook structured data, person schema,
 * thought leader schema
 */
import { BRAND, BRAND_ORGANIZATION_ID, BRAND_URL } from "~/lib/shared/brand";
import { DEFAULT_OG_IMAGE } from "~/lib/shared/meta";

type Faq = { readonly question: string; readonly answer: string };

type ThoughtLeader = {
  readonly name: string;
  readonly organization: string | null;
  readonly website: string | null;
};

export type PlaybookJsonLdInput = {
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  readonly thoughtLeader: ThoughtLeader;
  readonly faqs: ReadonlyArray<Faq>;
  readonly publishedAt: Date | null;
  readonly updatedAt: Date | null;
};

export function buildPlaybookJsonLd(input: PlaybookJsonLdInput) {
  const url = `${BRAND_URL}/coaching-business/playbooks/${input.slug}`;
  const articleId = `${url}#article`;
  const personId = `${url}#author`;
  const breadcrumbId = `${url}#breadcrumb`;

  const datePublished = input.publishedAt?.toISOString();
  const dateModified =
    input.updatedAt?.toISOString() ?? input.publishedAt?.toISOString();

  const personNode: Record<string, unknown> = {
    "@type": "Person",
    "@id": personId,
    name: input.thoughtLeader.name,
    ...(input.thoughtLeader.organization
      ? {
          affiliation: {
            "@type": "Organization",
            name: input.thoughtLeader.organization,
          },
        }
      : {}),
    ...(input.thoughtLeader.website
      ? {
          url: input.thoughtLeader.website,
          sameAs: [input.thoughtLeader.website],
        }
      : {}),
  };

  const graph: Array<Record<string, unknown>> = [
    {
      "@type": "Organization",
      "@id": BRAND_ORGANIZATION_ID,
      name: BRAND.name,
      url: BRAND_URL,
      logo: {
        "@type": "ImageObject",
        url: BRAND.logo,
      },
    },
    {
      "@type": "Article",
      "@id": articleId,
      headline: input.title,
      description: input.description,
      url,
      // Playbooks have no hero-image column, so the Article image is always the
      // brand card. Article `image` must be absolute; DEFAULT_OG_IMAGE is.
      image: DEFAULT_OG_IMAGE,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: { "@id": BRAND_ORGANIZATION_ID },
      publisher: { "@id": BRAND_ORGANIZATION_ID },
      about: { "@id": personId },
      creator: { "@id": personId },
      ...(datePublished ? { datePublished } : {}),
      ...(dateModified ? { dateModified } : {}),
      inLanguage: "en",
      articleSection: "Coach Business Builder",
    },
    personNode,
    {
      "@type": "BreadcrumbList",
      "@id": breadcrumbId,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: BRAND_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Coaching Business",
          item: `${BRAND_URL}/coaching-business`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Playbooks",
          item: `${BRAND_URL}/coaching-business/playbooks`,
        },
        { "@type": "ListItem", position: 4, name: input.title, item: url },
      ],
    },
    ...(input.faqs.length > 0
      ? [
          {
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            mainEntity: input.faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          },
        ]
      : []),
  ];

  return { "@context": "https://schema.org", "@graph": graph };
}
